/**
 * DatePicker — Custom Popover-basierter Datepicker (Sprint-16-Brand).
 *
 * Motivation: der native `<input type="date">` ist visuell inkonsistent quer
 * durch Browser + hässlich. Diese Component nutzt react-day-picker in einem
 * Radix-Popover mit Sprint-16-Styling.
 *
 * Input-Trigger sieht aus wie ein normales Input mit CalendarIcon-Prefix.
 * Popover zeigt Monats-Kalender + Prev/Next-Navigation.
 *
 * Value: ISO-String „YYYY-MM-DD" (matches HTML-native date-input-Convention).
 */
import * as Popover from '@radix-ui/react-popover';
import { CalendarDays } from 'lucide-react';
import { forwardRef, useState, type ReactNode } from 'react';
import { DayPicker, type Matcher } from 'react-day-picker';
import { de } from 'date-fns/locale/de';
import { cn } from '@/shared/lib/cn';
import { DayPickerChevron, dayPickerClassNames } from './dayPickerTheme';

// Der Einzeldatum-Kalender behaelt seine bisherigen Masse und die Navigation oben rechts.
const DATE_PICKER_CLASS_NAMES = {
  ...dayPickerClassNames,
  root: 'text-ink',
  nav: 'absolute right-3 top-3 flex items-center gap-1',
  weekday:
    'w-8 text-ink-faint text-[10px] font-semibold uppercase tracking-[0.14em] py-1 text-center',
  day: 'w-8 h-8 p-0 text-center text-[13px] tabular-nums',
};

function toIso(d: Date | undefined): string {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromIso(iso: string | undefined | null): Date | undefined {
  if (!iso) return undefined;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function buildDisabledMatchers(
  minD: Date | undefined,
  maxD: Date | undefined,
): Matcher[] | undefined {
  const matchers: Matcher[] = [];
  if (minD) matchers.push({ before: minD });
  if (maxD) matchers.push({ after: maxD });
  return matchers.length > 0 ? matchers : undefined;
}

function formatDisplay(iso: string | null | undefined, placeholder: string): ReactNode {
  const d = fromIso(iso ?? undefined);
  if (!d) return <span className="text-ink-faint">{placeholder}</span>;
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

export interface DatePickerProps {
  value: string | null;
  onChange: (iso: string | null) => void;
  /** Ersetzt die Datumsanzeige im Trigger — etwa ein Zeitraum statt eines einzelnen Tages. */
  display?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  minDate?: string;
  maxDate?: string;
  /** aria-invalid Passthrough für FormField */
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-label'?: string;
  'aria-describedby'?: string;
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      display,
      placeholder = 'TT.MM.JJJJ',
      disabled,
      className,
      id,
      minDate,
      maxDate,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const selected = fromIso(value);
    const minD = fromIso(minDate);
    const maxD = fromIso(maxDate);

    return (
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            ref={ref}
            type="button"
            id={id}
            disabled={disabled}
            className={cn(
              'border-line text-ink flex w-full items-center gap-2 rounded-md border bg-white px-4 py-2.5 text-left text-[14px]',
              'duration-fast transition-colors',
              'focus:ring-sage focus:border-transparent focus:outline-none focus:ring-2 focus:ring-inset',
              'disabled:bg-card-alt disabled:pointer-events-none disabled:opacity-40',
              'aria-invalid:border-sealwax aria-invalid:ring-sealwax/20',
              className,
            )}
            {...props}
          >
            <CalendarDays size={14} strokeWidth={2} className="text-ink-faint shrink-0" />
            <span className="flex-1 truncate">{display ?? formatDisplay(value, placeholder)}</span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={6}
            className={cn(
              // pointer-events-auto: das Popover rendert in einem Body-Portal; ein umgebender
              // Radix-Modal-Dialog sperrt body{pointer-events:none} → sonst waere der Kalender
              // sichtbar, aber nicht klickbar. z-[60] haelt ihn ueber dem Dialog (z-50).
              'bg-card border-line/50 shadow-card z-60 pointer-events-auto rounded-lg border p-3',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            )}
          >
            <DayPicker
              mode="single"
              locale={de}
              selected={selected}
              onSelect={(d) => {
                onChange(d ? toIso(d) : null);
                if (d) setOpen(false);
              }}
              disabled={buildDisabledMatchers(minD, maxD)}
              weekStartsOn={1}
              showOutsideDays
              classNames={DATE_PICKER_CLASS_NAMES}
              components={{ Chevron: DayPickerChevron }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  },
);
DatePicker.displayName = 'DatePicker';
